"use client";

import { c as _c } from "react/compiler-runtime";
import { Flame, Trophy } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import Link from "next/link";
import { useLoungePoints } from "@/hooks/useLoungePoints";
import { useUser } from "@/hooks/useUser";

// Compact points pill for sidebar footers; links to the member profile.
export function SidebarLoungePoints() {
  const $ = _c(25);
  const {
    user
  } = useUser();
  const {
    data
  } = useLoungePoints();
  const reduceMotion = useReducedMotion();
  if (!user || !data) {
    return null;
  }
  const {
    stats
  } = data;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Trophy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = stats.totalPoints;
  let t2;
  if ($[1] !== reduceMotion) {
    t2 = reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      transform: "translateY(100%)"
    };
    $[1] = reduceMotion;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 1,
      transform: "translateY(0%)"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== reduceMotion) {
    t4 = reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      transform: "translateY(-100%)"
    };
    $[4] = reduceMotion;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      duration: 0.25,
      ease: [0.16, 1, 0.3, 1]
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== stats.totalPoints) {
    t6 = stats.totalPoints.toLocaleString();
    $[7] = stats.totalPoints;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== stats.totalPoints || $[10] !== t2 || $[11] !== t4 || $[12] !== t6) {
    t7 = <span className="relative inline-flex overflow-hidden font-semibold tabular-nums"><AnimatePresence mode="popLayout" initial={false}><motion.span key={t1} initial={t2} animate={t3} exit={t4} transition={t5}>{t6}</motion.span></AnimatePresence></span>;
    $[9] = stats.totalPoints;
    $[10] = t2;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== stats.level) {
    t8 = <span className="text-muted-foreground">points · Lv {stats.level}</span>;
    $[14] = stats.level;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== reduceMotion || $[17] !== stats.currentStreak) {
    t9 = stats.currentStreak > 1 ? <motion.span key="streak" initial={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.45
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={reduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      scale: 0.45
    }} transition={{
      duration: 0.16,
      ease: "easeOut"
    }} className="ml-auto inline-flex items-center gap-0.5 text-muted-foreground"><Flame className="h-3.5 w-3.5 text-orange-500" />{stats.currentStreak}</motion.span> : null;
    $[16] = reduceMotion;
    $[17] = stats.currentStreak;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <AnimatePresence initial={false}>{t9}</AnimatePresence>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t7 || $[23] !== t8) {
    t11 = <Link href="/profile" className="mx-2 mb-1 flex items-center gap-2 rounded-md bg-sidebar-accent/60 px-3 py-2 text-xs text-sidebar-foreground transition-colors hover:bg-sidebar-accent group-data-[collapsible=icon]:hidden">{t0}{t7}{t8}{t10}</Link>;
    $[21] = t10;
    $[22] = t7;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}